// src/pages/Admin/Users/EditUserModal.jsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaCamera,
  FaCloudUploadAlt,
  FaCrop,
  FaEnvelope,
  FaIdBadge,
  FaImage,
  FaInfoCircle,
  FaLink,
  FaSave,
  FaShieldAlt,
  FaSpinner,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { updateUser, getUserById } from "../../../services/userServices";
import { toast } from "react-toastify";

import {
  uploadUserPhoto,
  cropUserPhoto,
  toAbsoluteUserPhotoUrl,
} from "../../../services/userPhotoServices";

import CropPhotoModal, {
  toMasterUrl,
} from "../../../components/CropPhotoModal/CropPhotoModal";

const DEFAULT_AVATAR = "/img/user_avatar.jpg";

const withBust = (url, version) => {
  if (!url) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  return `${url}${url.includes("?") ? "&" : "?"}v=${version}`;
};

const inputClass =
  "min-h-[48px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-violet-400 dark:focus:ring-violet-950/60";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

const roleLabel = (roleId) => {
  const id = Number(roleId);

  if (id === 1) return "Administrateur";
  if (id === 3) return "Enseignant";
  if (id === 2) return "Utilisateur";

  return "Rôle non défini";
};

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
      <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm">
            <Icon className="text-sm" />
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">
              {title}
            </h4>

            {description && (
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">{children}</div>
    </section>
  );
}

function PreviewLine({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-800 dark:text-white">
        {value || "—"}
      </p>
    </div>
  );
}

function UserPreviewCard({
  formData,
  previewSrc,
  imgOk,
  onImageError,
  onOpenCrop,
  canCrop,
  uploadMode,
  onOpenFilePicker,
}) {
  const fullName = `${formData.prenom || ""} ${formData.nom || ""}`.trim();

  return (
    <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-950 dark:bg-slate-900">
      <div className="bg-gradient-to-br from-violet-600 to-blue-600 p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
            <FaUser className="text-xl" />
          </div>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-white/70">
              Compte utilisateur
            </p>
            <p className="truncate text-lg font-bold">
              {fullName || "Utilisateur"}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div
          className={`mx-auto h-40 w-40 overflow-hidden rounded-[2rem] bg-slate-100 shadow-inner ring-4 ring-white dark:bg-slate-800 dark:ring-slate-900 ${
            uploadMode === "upload" ? "cursor-pointer" : ""
          }`}
          onClick={(event) => {
            event.stopPropagation();
            if (uploadMode === "upload") onOpenFilePicker?.(event);
          }}
          title={
            uploadMode === "upload"
              ? "Cliquer pour choisir une image"
              : "Aperçu utilisateur"
          }
        >
          <img
            src={previewSrc}
            alt={fullName || "Utilisateur"}
            onError={onImageError}
            className="h-full w-full object-cover"
            draggable={false}
          />
        </div>

        {!imgOk && (
          <div className="mt-3 rounded-2xl bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700 ring-1 ring-orange-100 dark:bg-orange-950/30 dark:text-orange-300 dark:ring-orange-900">
            Impossible de charger l’image. L’avatar par défaut est utilisé.
          </div>
        )}

        <div className="mt-4 text-center">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">
            {fullName || "Utilisateur"}
          </h3>

          <span className="mt-2 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
            {roleLabel(formData.role_id)}
          </span>
        </div>

        {canCrop && (
          <button
            type="button"
            onClick={onOpenCrop}
            className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl bg-violet-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700"
          >
            <FaCrop />
            Recadrer / Zoom
          </button>
        )}

        <div className="mt-4 grid grid-cols-1 gap-2">
          <PreviewLine label="Email" value={formData.email || "—"} />
          <PreviewLine label="Rôle" value={roleLabel(formData.role_id)} />
        </div>
      </div>
    </section>
  );
}

function PhotoManagerCard({
  formData,
  uploadMode,
  setUploadMode,
  handleChange,
  openFilePicker,
  fileInputRef,
  handleFileChange,
  isUploading,
  uploadProgress,
  outputFormat,
  setOutputFormat,
}) {
  return (
    <SectionCard
      icon={FaCamera}
      title="Photo utilisateur"
      description="Utilisez une URL ou envoyez une image depuis votre appareil."
    >
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Mode photo</label>

          <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
            <button
              type="button"
              onClick={() => setUploadMode("url")}
              disabled={isUploading}
              className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition ${
                uploadMode === "url"
                  ? "bg-violet-600 text-white shadow-sm"
                  : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
              }`}
            >
              <FaLink />
              URL
            </button>

            <button
              type="button"
              onClick={() => setUploadMode("upload")}
              disabled={isUploading}
              className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition ${
                uploadMode === "upload"
                  ? "bg-violet-600 text-white shadow-sm"
                  : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
              }`}
            >
              <FaCloudUploadAlt />
              Upload
            </button>
          </div>
        </div>

        {uploadMode === "url" ? (
          <div>
            <label className={labelClass}>Lien de la photo</label>

            <div className="relative">
              <FaLink className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                name="photoURL"
                value={formData.photoURL}
                onChange={handleChange}
                placeholder="https://… ou /uploads/…"
                className={`${inputClass} pl-9`}
                disabled={isUploading}
              />
            </div>
          </div>
        ) : (
          <div className="rounded-3xl border-2 border-dashed border-violet-200 bg-slate-50 p-4 text-center transition hover:border-violet-400 hover:bg-violet-50/60 dark:border-violet-900 dark:bg-slate-950/40 dark:hover:bg-violet-950/20">
            <input
              ref={fileInputRef}
              id="user-photo-file"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploading}
            />

            <FaCloudUploadAlt className="mx-auto text-4xl text-violet-600 dark:text-violet-300" />

            <p className="mt-2 text-sm font-bold text-slate-800 dark:text-white">
              Envoyer une image
            </p>

            <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              PNG, JPG ou WEBP. L’image sera traitée en carré 400×400.
            </p>

            <button
              type="button"
              onClick={openFilePicker}
              disabled={isUploading}
              className="mt-4 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isUploading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Upload…
                </>
              ) : (
                <>
                  <FaImage />
                  Choisir un fichier
                </>
              )}
            </button>

            <div className="mt-4">
              <label className={labelClass}>Format de sortie</label>

              <select
                className={inputClass}
                value={outputFormat}
                onChange={(event) => setOutputFormat(event.target.value)}
                disabled={isUploading}
              >
                <option value="webp">WEBP recommandé</option>
                <option value="jpg">JPG</option>
                <option value="png">PNG</option>
              </select>
            </div>

            {isUploading && (
              <div className="mt-4 rounded-2xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                <div className="mb-2 flex items-center justify-between text-xs font-bold text-slate-500 dark:text-slate-400">
                  <span>Upload en cours</span>
                  <span>{uploadProgress}%</span>
                </div>

                <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                  <div
                    className="h-full rounded-full bg-violet-600 transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-start gap-2 rounded-2xl bg-blue-50 px-3 py-2 text-xs text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:ring-blue-900">
          <FaInfoCircle className="mt-0.5 shrink-0" />
          Le recadrage est disponible dès qu’une photo utilisateur existe.
        </div>
      </div>
    </SectionCard>
  );
}

export default function EditUserModal({
  isOpen,
  onClose,
  refreshList,
  userId,
}) {
  const dialogRef = useRef(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    role_id: "",
    photoURL: "",
  });

  const [loadingUser, setLoadingUser] = useState(false);
  const [saving, setSaving] = useState(false);

  const [imgOk, setImgOk] = useState(true);
  const [uploadMode, setUploadMode] = useState("url");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [imgVersion, setImgVersion] = useState(0);
  const [isCropOpen, setIsCropOpen] = useState(false);

  const handleClose = useCallback(() => {
    if (saving || isUploading) return;
    onClose?.();
  }, [onClose, saving, isUploading]);

  useEffect(() => {
    if (!isOpen || !userId) return;

    const loadUser = async () => {
      try {
        setLoadingUser(true);

        const data = await getUserById(userId);

        setFormData({
          nom: data?.nom || "",
          prenom: data?.prenom || "",
          email: data?.email || "",
          role_id: data?.role_id ?? "",
          photoURL: data?.photoURL || "",
        });

        setImgOk(true);
        setUploadMode("url");
        setUploadProgress(0);
        setIsUploading(false);
        setIsCropOpen(false);
        setImgVersion((value) => value + 1);
      } catch (error) {
        console.error("Erreur lors du chargement de l'utilisateur:", error);
        toast.error("Erreur lors du chargement des données de l'utilisateur.");
      } finally {
        setLoadingUser(false);
      }
    };

    loadUser();
  }, [isOpen, userId]);

  useEffect(() => {
    if (!isOpen) return;

    const root = window.document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();

        if (isCropOpen) {
          setIsCropOpen(false);
          return;
        }

        handleClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    const timer = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
      root.style.overflow = previousOverflow;
    };
  }, [isOpen, isCropOpen, handleClose]);

  if (!isOpen) return null;

  const absolutePhoto = toAbsoluteUserPhotoUrl(formData.photoURL);

  const previewSrc =
    formData.photoURL && imgOk
      ? withBust(absolutePhoto, imgVersion)
      : withBust(DEFAULT_AVATAR, imgVersion);

  const canSubmit =
    !saving &&
    !isUploading &&
    !!String(formData.nom || "").trim() &&
    !!String(formData.prenom || "").trim() &&
    !!String(formData.email || "").trim() &&
    !!formData.role_id;

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: name === "role_id" ? (value ? Number(value) : "") : value,
    }));

    if (name === "photoURL") {
      setImgOk(true);
      setImgVersion((value) => value + 1);
    }
  };

  const openFilePicker = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    event.stopPropagation();

    const file = event.target.files?.[0];

    if (!file) return;

    if (!userId) {
      toast.error("ID utilisateur manquant.");
      return;
    }

    const okType = /^image\/(png|jpe?g|webp)$/i.test(file.type);

    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux : maximum 10 Mo.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const response = await uploadUserPhoto(userId, file, {
        format: outputFormat,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;

          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          setUploadProgress(percent);
        },
      });

      const newThumbUrl =
        response?.photo_url ||
        response?.photoURL ||
        response?.imageUrl ||
        response?.url ||
        response?.path ||
        response?.data?.photo_url ||
        response?.data?.photoURL ||
        response?.data?.imageUrl ||
        response?.data?.url ||
        response?.data?.path;

      if (newThumbUrl) {
        setFormData((previous) => ({
          ...previous,
          photoURL: newThumbUrl,
        }));

        setImgOk(true);
        setImgVersion((value) => value + 1);

        toast.success("Photo uploadée et traitée.");
      } else {
        console.warn("Réponse upload inattendue:", response);
        toast.warn("Upload terminé mais URL non renvoyée.");
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error(error);
      toast.error(`Erreur upload : ${error?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isUploading) {
      toast.info("Patiente, l'upload est en cours…");
      return;
    }

    if (!canSubmit) {
      toast.warn("Merci de compléter les champs obligatoires.");
      return;
    }

    try {
      setSaving(true);

      await updateUser(userId, {
        ...formData,
        role_id: Number(formData.role_id),
      });

      refreshList?.();
      onClose?.();

      toast.success("Utilisateur mis à jour avec succès.");
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'utilisateur", error);
      toast.error("Erreur lors de la mise à jour de l'utilisateur.");
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <>
      <div
        className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Éditer un utilisateur"
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            handleClose();
          }
        }}
      >
        <div
          ref={dialogRef}
          className="relative flex max-h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-t-[2rem] bg-slate-50 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 sm:max-h-[90dvh] sm:rounded-[2rem]"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-violet-600 to-pink-600 px-4 py-5 text-white sm:px-6">
            <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

            <div className="relative flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/75">
                  Gestion utilisateur
                </p>

                <h3 className="mt-1 text-xl font-bold text-white sm:text-2xl">
                  Éditer un utilisateur
                </h3>

                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/85">
                  Mettez à jour l’identité, le rôle et la photo du compte.
                </p>
              </div>

              <button
                type="button"
                onClick={handleClose}
                disabled={saving || isUploading}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 disabled:opacity-60"
                title="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="user-modal-scroll min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
                <aside className="space-y-5">
                  <UserPreviewCard
                    formData={formData}
                    previewSrc={previewSrc}
                    imgOk={imgOk}
                    onImageError={() => setImgOk(false)}
                    onOpenCrop={() => setIsCropOpen(true)}
                    canCrop={!!formData.photoURL}
                    uploadMode={uploadMode}
                    onOpenFilePicker={openFilePicker}
                  />

                  <PhotoManagerCard
                    formData={formData}
                    uploadMode={uploadMode}
                    setUploadMode={setUploadMode}
                    handleChange={handleChange}
                    openFilePicker={openFilePicker}
                    fileInputRef={fileInputRef}
                    handleFileChange={handleFileChange}
                    isUploading={isUploading}
                    uploadProgress={uploadProgress}
                    outputFormat={outputFormat}
                    setOutputFormat={setOutputFormat}
                  />
                </aside>

                <main className="space-y-5">
                  <SectionCard
                    icon={FaIdBadge}
                    title="Identité"
                    description="Informations principales du compte utilisateur."
                  >
                    {loadingUser ? (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {[...Array(4)].map((_, index) => (
                          <div
                            key={index}
                            className="h-12 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label className={labelClass}>
                            Nom <span className="text-pink-600">*</span>
                          </label>

                          <div className="relative">
                            <FaUser className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                            <input
                              type="text"
                              name="nom"
                              value={formData.nom}
                              onChange={handleChange}
                              required
                              disabled={saving || isUploading}
                              className={`${inputClass} pl-9`}
                            />
                          </div>
                        </div>

                        <div>
                          <label className={labelClass}>
                            Prénom <span className="text-pink-600">*</span>
                          </label>

                          <div className="relative">
                            <FaUser className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                            <input
                              type="text"
                              name="prenom"
                              value={formData.prenom}
                              onChange={handleChange}
                              required
                              disabled={saving || isUploading}
                              className={`${inputClass} pl-9`}
                            />
                          </div>
                        </div>

                        <div className="sm:col-span-2">
                          <label className={labelClass}>
                            Email <span className="text-pink-600">*</span>
                          </label>

                          <div className="relative">
                            <FaEnvelope className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              disabled={saving || isUploading}
                              className={`${inputClass} pl-9`}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </SectionCard>

                  <SectionCard
                    icon={FaShieldAlt}
                    title="Rôle et droits"
                    description="Définissez le rôle principal de l’utilisateur."
                  >
                    <div>
                      <label className={labelClass}>
                        Rôle <span className="text-pink-600">*</span>
                      </label>

                      <select
                        name="role_id"
                        value={formData.role_id}
                        onChange={handleChange}
                        required
                        disabled={saving || isUploading || loadingUser}
                        className={inputClass}
                      >
                        <option value="">— Sélectionner un rôle —</option>
                        <option value="1">Administrateur</option>
                        <option value="2">Utilisateur</option>
                        <option value="3">Enseignant</option>
                      </select>

                      <div className="mt-4 rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                        <p className="text-sm font-bold text-slate-800 dark:text-white">
                          Rôle sélectionné : {roleLabel(formData.role_id)}
                        </p>

                        <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                          Administrateur : accès complet. Enseignant : gestion
                          pédagogique. Utilisateur : accès adhérent classique.
                        </p>
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard
                    icon={FaInfoCircle}
                    title="Résumé du compte"
                    description="Contrôle rapide avant enregistrement."
                  >
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <PreviewLine
                        label="Nom"
                        value={formData.nom?.toUpperCase() || "—"}
                      />
                      <PreviewLine label="Prénom" value={formData.prenom || "—"} />
                      <PreviewLine
                        label="Rôle"
                        value={roleLabel(formData.role_id)}
                      />
                    </div>
                  </SectionCard>
                </main>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-6">
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={saving || isUploading}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  <FaTimes />
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Enregistrement…
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Mettre à jour
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        <style>{`
          .user-modal-scroll::-webkit-scrollbar {
            width: 10px;
          }

          .user-modal-scroll::-webkit-scrollbar-thumb {
            background: rgba(148, 163, 184, 0.45);
            border-radius: 9999px;
            border: 3px solid transparent;
            background-clip: content-box;
          }

          .user-modal-scroll::-webkit-scrollbar-track {
            background: transparent;
          }

          .dark .user-modal-scroll::-webkit-scrollbar-thumb {
            background: rgba(148, 163, 184, 0.35);
            border: 3px solid transparent;
            background-clip: content-box;
          }
        `}</style>
      </div>

      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={userId}
        imageUrl={
          formData.photoURL
            ? withBust(
                toAbsoluteUserPhotoUrl(toMasterUrl(formData.photoURL)),
                imgVersion
              )
            : ""
        }
        onCropped={(newUrl) => {
          setFormData((previous) => ({
            ...previous,
            photoURL: newUrl,
          }));

          setImgOk(true);
          setImgVersion((value) => value + 1);
        }}
        format="webp"
        cropAction={cropUserPhoto}
      />
    </>,
    window.document.body
  );
}